import React, {useCallback, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useAuth} from '@/contexts/AuthContext';
import {useToast} from '@/hooks/use-toast';

const SSOHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {signInWithCentific} = useAuth();
  const {toast} = useToast();

  const handleSSOTokens = useCallback(async () => {
    try {
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const expiresAt = searchParams.get('expires_at');
      const userData = searchParams.get('user_data');

      if (!accessToken) {
        return;
      }

      let expiresIn: number | undefined;
      if (expiresAt) {
        const expiryTime = new Date(expiresAt).getTime();
        const currentTime = Date.now();
        expiresIn = Math.floor((expiryTime - currentTime) / 1000);
      }

      const {error} = await signInWithCentific(
        accessToken,
        accessToken, // id_token
        refreshToken || undefined,
        expiresIn
      );

      if (error) {
        console.error('[SSOHandler] SSO login failed:', error);
        toast({
          title: 'SSO Login Failed',
          description: error,
          variant: 'destructive',
        });
        navigate('/auth');
        return;
      }
      toast({
        title: 'SSO Login Successful',
        description: 'You have been successfully logged in via SSO.',
      });

      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
      navigate('/');
    } catch (error) {
      console.error('[SSOHandler] Error processing SSO tokens:', error);
      toast({
        title: 'SSO Login Failed',
        description: 'An error occurred while processing SSO login.',
        variant: 'destructive',
      });
      navigate('/auth');
    }
  }, [searchParams, signInWithCentific, toast, navigate]);

  // ✅ Now call it inside the effect
  useEffect(() => {
    handleSSOTokens();
  }, [handleSSOTokens]);

  return null;
};

export default SSOHandler;
